import { useAuthenticationStore } from './authentication.store';
import { jwtDecode } from 'jwt-decode';
import MeService from '@/core/shared/me/me.service';
import { AuthenticationPersistence } from '@/core/shared/authentication/authentication.persistence';
import { InternalError } from '@/core/shared/errors/internal.error';
import { getExponentialBackoffWithJitter } from '@/utils/backoff/backoff.util';
import { ApiRequestFatalError } from '../errors/api-request-fatal.error';
import { ErrorService } from '../errors/error.service';
// The store is used directly rather than through TemplateService: that service pulls in
// ConversationIndexService, whose persistence layer imports useApi, which imports this
// file — importing it here would close that cycle.
import { useTemplate } from '@/core/conversations/template/template.store';

// Module-scoped so concurrent AuthenticationService instances share the lock
// and we don't burn duplicate refresh-token rotations on the same wake-up.
let inflightIdTokenRefresh: Promise<void> | null = null;

// Module-scoped so multiple service instances don't stack duplicate handlers.
let resumeListenersAttached = false;

export default class AuthenticationService {
  private readonly store = useAuthenticationStore();
  private readonly persistence = new AuthenticationPersistence();
  private readonly meService = new MeService();

  /** "the idToken is currently usable as a bearer token" — strict. */
  get isAuthenticated(): boolean {
    let isLoggedIn = false;

    if (this.store.idToken && this.store.idTokenExpiresAt) {
      if (Date.now() < this.store.idTokenExpiresAt) {
        isLoggedIn = true;
      }
    }

    return isLoggedIn;
  }

  /**
   * True when one `/auth/token` exchange away from authenticated.
   * Looser than `isAuthenticated` so guards can call `ensureFreshIdToken`
   * before deciding to bounce a user to UI2.
   */
  get isSessionRecoverable(): boolean {
    return this.isAuthenticated || this.hasValidRefreshToken;
  }

  /**
   * True when the id token is missing, already expired, or inside
   * `idTokenRefreshBuffer` of expiry — the condition under which any caller
   * (a proactive resume/timer refresh, or a request about to go out) should
   * exchange for a new one before relying on it.
   */
  get idTokenNeedsRefresh(): boolean {
    const expiresAt = this.store.idTokenExpiresAt;
    return !expiresAt || Date.now() + idTokenRefreshBuffer >= expiresAt;
  }

  get isAccessTokenValid(): boolean {
    let isValid = false;

    if (this.store.accessToken && this.store.accessTokenExpiresAt) {
      // consider the token invalid if it expires in the next 25 seconds
      const buffer = 25 * 1000;
      const effectiveExpiresAt = this.store.accessTokenExpiresAt - buffer;
      if (Date.now() < effectiveExpiresAt) {
        isValid = true;
      }
    }

    return isValid;
  }

  get accessToken(): string | undefined {
    return this.store.accessToken;
  }

  setAccessToken(token: string): void {
    const decodedToken = jwtDecode(token);
    this.store.accessToken = token;

    const expireTs = decodedToken.exp
      ? decodedToken.exp * 1000
      : // if no exp is set, use maximum refresh interval
        new Date().getTime() + accessTokenRefreshMaximum;

    this.store.accessTokenExpiresAt = expireTs;
    this.scheduleAccessTokenRefresh(expireTs);
  }

  get idToken(): string | undefined {
    return this.store.idToken;
  }

  setIdToken(token?: string | null): void {
    if (token) {
      this.store.idToken = token;
    }

    if (!this.store.idToken) {
      return;
    }

    const decodedToken = jwtDecode(this.store.idToken);
    const expireTs = decodedToken.exp
      ? decodedToken.exp * 1000
      : // if no exp is set, use maximum refresh interval
        new Date().getTime() + idTokenRefreshMaximum;

    this.store.idTokenExpiresAt = expireTs;
    this.scheduleIdTokenRefresh(expireTs);
  }

  get refreshToken(): string | undefined {
    return this.store.refreshToken;
  }

  get refreshTokenExpiresAt(): number | undefined {
    return this.store.refreshTokenExpiresAt;
  }

  setRefreshToken(token: string, expiresAt: number): void {
    this.store.refreshToken = token;
    this.store.refreshTokenExpiresAt = expiresAt;
  }

  private get hasValidRefreshToken(): boolean {
    return (
      !!this.store.refreshToken &&
      typeof this.store.refreshTokenExpiresAt === 'number' &&
      Date.now() < this.store.refreshTokenExpiresAt
    );
  }

  async logout(): Promise<void> {
    const refreshToken = this.store.refreshToken;
    if (refreshToken) {
      // Fire-and-forget revoke so a slow /auth/logout can't stall local logout.
      this.persistence.revokeSession(refreshToken).catch(() => undefined);
    }
    this.store.clear();
    this.meService.clear();
    // Templates are per-user and their store is persisted without a user key, so they
    // must be dropped on logout or the next user of this browser can read — and insert
    // into their own candidate message — the previous user's custom templates.
    useTemplate().clear();
  }

  scheduleAccessTokenRefresh(expiresAt: number): void {
    const refreshTime = this.calculateAccessTokenRefreshTime(expiresAt);

    this.store.clearAccessTokenTimer();
    this.store.accessTokenRefreshTimerId = setTimeout(() => {
      // refreshAccessToken self-reschedules its own retries
      // (scheduleAccessTokenRefreshRetry); we report so a chain of silent
      // failures is still visible in Sentry.
      this.refreshAccessToken().catch((error: unknown) => {
        ErrorService.captureException(error);
      });
    }, refreshTime);
  }

  scheduleAccessTokenRefreshRetry(retriesAttempted: number): void {
    const retryTime = this.calculateAccessTokenRefreshRetryTime(retriesAttempted);

    this.store.clearAccessTokenTimer();
    this.store.accessTokenRefreshTimerId = setTimeout(() => {
      this.refreshAccessToken(retriesAttempted + 1).catch((error: unknown) => {
        ErrorService.captureException(error);
      });
    }, retryTime);
  }

  scheduleIdTokenRefresh(expiresAt: number): void {
    const refreshTime = this.calculateIdTokenRefreshTime(expiresAt);

    this.store.clearIdTokenTimer();
    this.store.idTokenRefreshTimerId = setTimeout(() => {
      // Route through ensureFreshIdToken (not refreshIdToken) so the
      // in-flight dedupe catches us when the timer races a resume listener
      // or a 401 retry — otherwise two parallel /auth/token calls with the
      // same refresh token would trip the server's reuse detection.
      this.ensureFreshIdToken().then(() => {
        if (!this.isAuthenticated) {
          // Re-arm so a transient failure (offline, brief 5xx) doesn't
          // silently kill the proactive chain. Bounded below by
          // idTokenRefreshMinimum and by the user's next request.
          this.scheduleIdTokenRefreshRetry();
        }
      });
    }, refreshTime);
  }

  private scheduleIdTokenRefreshRetry(): void {
    this.store.clearIdTokenTimer();
    this.store.idTokenRefreshTimerId = setTimeout(() => {
      void this.ensureFreshIdToken();
    }, idTokenRefreshMinimum);
  }

  /**
   * Idempotent, concurrency-safe refresh-if-stale helper. Safe to call from
   * resume listeners, route guards, and 401 retries — concurrent calls
   * dedupe onto the same in-flight exchange. Never throws; callers should
   * re-inspect `isAuthenticated`/`isSessionRecoverable` to confirm success.
   */
  async ensureFreshIdToken(): Promise<void> {
    if (inflightIdTokenRefresh) return inflightIdTokenRefresh;

    if (!this.idTokenNeedsRefresh) return;

    // Cold boot (pre UI2-handoff): nothing to refresh; bootstrap will handle.
    if (!this.store.idToken && !this.hasValidRefreshToken) return;

    inflightIdTokenRefresh = (async () => {
      try {
        await this.refreshIdToken();
      } catch (error) {
        // A definitive 401 surfaces as ApiRequestFatalError after a clean
        // logout+redirect — that's expected, not Sentry-worthy noise.
        if (!(error instanceof ApiRequestFatalError)) {
          ErrorService.captureException(error);
        }
      } finally {
        inflightIdTokenRefresh = null;
      }
    })();
    return inflightIdTokenRefresh;
  }

  /**
   * Wires resume listeners. `setTimeout`-only schedulers miss long idles
   * because browsers throttle background-tab timers and suspend them
   * entirely during device sleep — these events catch the wake-up.
   */
  initResumeListeners(): void {
    if (resumeListenersAttached) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    resumeListenersAttached = true;

    const trigger = (): void => {
      void this.ensureFreshIdToken();
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') trigger();
    });
    window.addEventListener('focus', trigger);
    window.addEventListener('online', trigger);
  }

  async refreshAccessToken(retriesAttempted: number = 0): Promise<void> {
    const employerId = this.meService.getSelectedEmployerId();
    if (!employerId) {
      // Fatal error. Retrying will not change the outcome.
      throw new InternalError('Could not get access token because employerId is unset.');
    }
    try {
      const token = await this.persistence.getAccessTokenWithEmployerContext(employerId);
      this.setAccessToken(token);
    } catch (error) {
      if (error instanceof ApiRequestFatalError) {
        // don't retry fatal errors that won't change with a retry
        throw error;
      } else {
        // retry on server errors and all other unknown errors
        ErrorService.captureMessage(
          `Failed to get access token for user ${this.meService.userProfile?.id}` +
            ` (${retriesAttempted} retries). Retrying...`,
        );
        this.scheduleAccessTokenRefreshRetry(retriesAttempted);
      }
    }
  }

  /**
   * Ensure that the access token is valid before returning it.
   * If the token is expired, refresh it and return the new one.
   */
  async getValidAccessToken(): Promise<string> {
    if (!this.isAccessTokenValid) {
      await this.refreshAccessToken();
    }
    if (!this.accessToken) {
      throw new InternalError('Access token is not set.');
    }

    return this.accessToken;
  }

  /**
   * Three-branch refresh-id-token flow:
   *
   * 1. **Primary path** — if the store has a non-expired refresh token,
   *    exchange it via `POST /auth/token`. Updates both `idToken` and
   *    `refreshToken` (rotated) atomically.
   * 2. **Legacy fallback** — if there's no refresh token in store but
   *    the current `idToken` is still valid, hit `GET /auth/refresh`
   *    which also returns a refresh token. After this call the user is
   *    on the primary path for subsequent refreshes.
   * 3. **Logout** — no usable refresh token and idToken is gone too;
   *    clear local state and bubble up so the caller can redirect to
   *    UI2.
   */
  async refreshIdToken(): Promise<void> {
    const refreshToken = this.store.refreshToken;
    if (this.hasValidRefreshToken && refreshToken) {
      const bundle = await this.exchangeOrLogoutOnFatal(() =>
        this.persistence.refreshSession(refreshToken),
      );
      this.setIdToken(bundle.token);
      this.setRefreshToken(bundle.refreshToken, bundle.refreshTokenExpiresAt);
      return;
    }

    if (this.isAuthenticated) {
      const bundle = await this.exchangeOrLogoutOnFatal(() =>
        this.persistence.refreshIdTokenLegacy(),
      );
      this.setIdToken(bundle.token);
      if (bundle.refreshToken && bundle.refreshTokenExpiresAt) {
        this.setRefreshToken(bundle.refreshToken, bundle.refreshTokenExpiresAt);
      }
      return;
    }

    await this.logout();
    throw new InternalError('Could not refresh id token: no valid credentials in store.');
  }

  /**
   * The auth-layer exchange (auth-exchange.client) bypasses useApi's
   * onFetchError, which used to logout + redirect to UI2 on any 401. Re-add
   * that terminal behavior here, but ONLY for a definitive auth failure
   * (ApiRequestFatalError = 401). Transient/network/5xx errors rethrow
   * untouched so the scheduler stays free to retry.
   */
  private async exchangeOrLogoutOnFatal<T>(exchange: () => Promise<T>): Promise<T> {
    try {
      return await exchange();
    } catch (error) {
      if (error instanceof ApiRequestFatalError) {
        await this.logout();
        this.meService.redirectToUI2('/employer/auth/logout');
      }
      throw error;
    }
  }

  private calculateAccessTokenRefreshTime(expiresAt: number) {
    const currentTime = new Date().getTime();
    const expiresIn = expiresAt - currentTime;
    const refreshIn = expiresIn - accessTokenRefreshBuffer;
    return Math.max(Math.min(refreshIn, accessTokenRefreshMaximum), accessTokenRefreshMinimum);
  }

  private calculateAccessTokenRefreshRetryTime(retriesAttempted: number) {
    const backoffBase = 5000; // 5 seconds
    return getExponentialBackoffWithJitter(
      backoffBase,
      retriesAttempted,
      accessTokenRefreshMaximum,
    );
  }

  private calculateIdTokenRefreshTime(expiresAt: number) {
    const currentTime = new Date().getTime();
    const expiresIn = expiresAt - currentTime;
    const refreshIn = expiresIn - idTokenRefreshBuffer;
    return Math.max(Math.min(refreshIn, idTokenRefreshMaximum), idTokenRefreshMinimum);
  }
}

/**
 * How many milliseconds before a token expires ought we try to refresh it?
 * Anything less than 30 seconds seems risky.
 */
export const accessTokenRefreshBuffer = 30 * 1000;
/**
 * Wait at least 30 seconds to refresh to avoid infinite loops with very short
 * token lifetimes
 */
export const accessTokenRefreshMinimum = 30 * 1000;
/**
 * Access tokens should be refreshed frequently. Make 4m30s the maximum
 * time between refreshes, even if the token lifetime is longer.
 * This is especially necessary for systems that have a system clock that is
 * not in sync with the server.
 */
export const accessTokenRefreshMaximum = 4.5 * 60 * 1000;

/**
 * How many milliseconds before a token expires ought we try to refresh it? (use
 * 7190000 in testing to get a refresh every 10 seconds)
 */
export const idTokenRefreshBuffer = 5 * 60 * 1000;
/**
 * Wait at least one minute to refresh to avoid infinite loops with very short
 * token lifetimes
 */
export const idTokenRefreshMinimum = 1 * 60 * 1000;
/**
 * If token lifetime is larger than what a 32-bit integer can hold, we will
 * have issues with setTimeout. Therefore, we set a maximum of 2 hours here.
 *
 * refresh token every 2 hours whether it needs it or not
 */
export const idTokenRefreshMaximum = 120 * 60 * 1000;
